import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ApolloProvider, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { IntlProvider, useIntl } from 'react-intl';
import client from '../../../lib/apolloClient';
import translations from '../../../lib/i18n/translations.json';
import SearchBar from '../../../../../ui/canon/SearchBar';

/**
 * HelpCenter - Full-page help center
 * Ported pixel-perfect from design_experiments/polymet-support-help-app/src/polymet/pages/help-center.tsx
 *
 * Route: /succession/help
 * Theming: Uses CSS variable tokens (--succession-*) for theme-aware colors.
 */

const GET_HELP_ARTICLES = gql`
  query GetHelpArticles($search: String, $category: String) {
    helpArticles(search: $search, category: $category) {
      id
      title
      description
      category
      md_body
      audience
      source_type
    }
    helpCategories
  }
`;

// Markdown renderer — handles headings, bold, italic, lists, tables, blockquotes, HR
const renderMarkdown = (content) => {
  if (!content) return '';

  const inlineFmt = (text) =>
    text
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold" style="color:var(--succession-text-primary,#111827)">$1</strong>')
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded text-sm" style="background:var(--succession-bg-secondary,#f3f4f6)">$1</code>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline" style="color:var(--succession-accent,#6366f1)">$1</a>');

  const lines = content.split('\n');
  const html = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // ── Table block (consecutive lines starting with |) ──
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      if (tableLines.length >= 2) {
        const parseRow = (row) => row.split('|').slice(1, -1).map((c) => c.trim());
        const isSep = (row) => /^[\s|:-]+$/.test(row.replace(/\|/g, ''));
        const headerCells = parseRow(tableLines[0]);
        const dataStart = isSep(tableLines[1]) ? 2 : 1;
        let t = '<div class="overflow-x-auto my-6"><table class="w-full border-collapse text-sm" style="border:1px solid var(--succession-border,#e5e7eb)"><thead><tr>';
        headerCells.forEach((c) => { t += `<th class="px-4 py-2.5 text-left font-semibold text-xs" style="border:1px solid var(--succession-border,#e5e7eb);background:var(--succession-bg-secondary,#f3f4f6);color:var(--succession-text-primary,#111827)">${inlineFmt(c)}</th>`; });
        t += '</tr></thead><tbody>';
        for (let r = dataStart; r < tableLines.length; r++) {
          t += '<tr>';
          parseRow(tableLines[r]).forEach((c) => { t += `<td class="px-4 py-2.5" style="border:1px solid var(--succession-border,#e5e7eb);color:var(--succession-text-secondary,#6b7280)">${inlineFmt(c)}</td>`; });
          t += '</tr>';
        }
        t += '</tbody></table></div>';
        html.push(t);
      }
      continue;
    }

    // ── Headings ──
    if (line.startsWith('### '))  { html.push(`<h3 class="text-xl font-semibold mt-6 mb-3" style="color:var(--succession-text-primary,#111827)">${inlineFmt(line.slice(4))}</h3>`);  i++; continue; }
    if (line.startsWith('## '))   { html.push(`<h2 class="text-2xl font-bold mt-8 mb-4" style="color:var(--succession-text-primary,#111827)">${inlineFmt(line.slice(3))}</h2>`);      i++; continue; }
    if (line.startsWith('# '))    { html.push(`<h1 class="text-3xl font-bold mt-8 mb-4" style="color:var(--succession-text-primary,#111827)">${inlineFmt(line.slice(2))}</h1>`);      i++; continue; }

    // ── Horizontal rule ──
    if (line.trim() === '---' || line.trim() === '***' || line.trim() === '___') { html.push('<hr class="my-8" style="border-color:var(--succession-border,#e5e7eb)" />'); i++; continue; }

    // ── Blockquote ──
    if (line.startsWith('> ')) { html.push(`<blockquote class="pl-4 my-4 leading-7" style="border-left:3px solid var(--succession-accent,#6366f1);color:var(--succession-text-secondary,#6b7280)">${inlineFmt(line.slice(2))}</blockquote>`); i++; continue; }

    // ── Unordered list item ──
    if (line.match(/^\s*- /)) { html.push(`<li class="ml-6 mb-1 list-disc" style="color:var(--succession-text-secondary,#6b7280)">${inlineFmt(line.replace(/^\s*- /, ''))}</li>`); i++; continue; }

    // ── Ordered list item ──
    if (line.match(/^\s*\d+\.\s/)) { html.push(`<li class="ml-6 mb-1 list-decimal" style="color:var(--succession-text-secondary,#6b7280)">${inlineFmt(line.replace(/^\s*\d+\.\s/, ''))}</li>`); i++; continue; }

    // ── Fully bold line ──
    if (line.startsWith('**') && line.endsWith('**')) { html.push(`<p class="mb-4 leading-7" style="color:var(--succession-text-primary,#111827)"><strong class="font-semibold">${line.slice(2, -2)}</strong></p>`); i++; continue; }

    // ── Empty line ──
    if (line.trim() === '') { html.push('<br />'); i++; continue; }

    // ── Paragraph with inline formatting ──
    html.push(`<p class="mb-4 leading-7" style="color:var(--succession-text-secondary,#6b7280)">${inlineFmt(line)}</p>`);
    i++;
  }

  return html.join('');
};

// Convert category slugs to title case: "getting-started" → "Getting Started", "deal_room" → "Deal Room"
const formatCategoryName = (slug) =>
  slug ? slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';

const categoryIcons = {
  'getting-started': 'M13 10V3L4 14h7v7l9-11h-7z',
  'valuation': 'M3 3v18h18 M7 16l4-4 4 4 5-6',
  'continuity-planning': 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  'deal-room': 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  'account-profile': 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 3a4 4 0 100 8 4 4 0 000-8z',
  'financial-metrics': 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
  'planbuilder': 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2 M9 5a2 2 0 002 2h2a2 2 0 002-2 M9 5a2 2 0 012-2h2a2 2 0 012 2 M9 14l2 2 4-4',
};

const iconBg = 'rgba(var(--succession-primary-rgb, 84,137,182), 0.1)';
const iconColor = 'var(--succession-primary, #5489B6)';
const cardStyle = {
  border: '1px solid var(--succession-border, #e5e7eb)',
  backgroundColor: 'var(--succession-bg-primary, #ffffff)',
  transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
};
const badgeStyle = {
  backgroundColor: 'var(--succession-bg-tertiary, #f3f4f6)',
  color: 'var(--succession-text-secondary, #6b7280)',
};

const CategoryIcon = ({ category, className }) => {
  const key = (category || '').toLowerCase();
  const d = categoryIcons[key] || categoryIcons['getting-started'];
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {d.split(' M').map((seg, i) => (
        <path key={i} d={i === 0 ? seg : `M${seg}`} />
      ))}
    </svg>
  );
};

// One row in a list of articles. Search results and the category list render
// the identical card, and keeping two copies of it is how the Help Center ended
// up with a category view that behaved unlike every other list on the page.
const ArticleRow = ({ article, onClick }) => (
  <div
    className="rounded-lg p-6 cursor-pointer"
    style={cardStyle}
    onClick={() => onClick(article)}
    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--succession-shadow, 0 4px 6px -1px rgba(0,0,0,0.1))'; }}
    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
  >
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: iconBg }}>
        <CategoryIcon category={article.category} className="w-6 h-6" style={{ color: iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h3 className="font-semibold text-lg" style={{ color: 'var(--succession-text-primary, #111827)' }}>{article.title}</h3>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium flex-shrink-0" style={badgeStyle}>{formatCategoryName(article.category)}</span>
        </div>
        <p className="text-sm line-clamp-2" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{article.description}</p>
      </div>
      <svg className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--succession-text-muted, #9ca3af)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
    </div>
  </div>
);

// supportEnabled mirrors the `support_requests` master toggle that gates
// shared/_intercom_client.html.erb. Without it the Contact Support controls
// below would render on tenants where no drawer is mounted to receive them.
CategoryIcon.propTypes = {
  category: PropTypes.string,
  className: PropTypes.string,
};

CategoryIcon.defaultProps = {
  category: '',
  className: '',
};

ArticleRow.propTypes = {
  article: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const HelpCenterInner = ({ supportEnabled = false }) => {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState('');
  // 'home' | 'list' | 'article'. In 'list', listCategory names the category being
  // listed, or is null for "Browse All Articles".
  const [viewMode, setViewMode] = useState('home');
  const [listCategory, setListCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  const { data, loading } = useQuery(GET_HELP_ARTICLES, {
    variables: { search: searchQuery || null },
  });

  const articles = data?.helpArticles || [];
  const categories = data?.helpCategories || [];

  const popularArticles = useMemo(() => articles.slice(0, 6), [articles]);

  const categorizedArticles = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      map[cat] = articles.filter((a) => a.category === cat);
    });
    return map;
  }, [articles, categories]);

  const listArticles = useMemo(() => (
    listCategory ? (categorizedArticles[listCategory] || []) : articles
  ), [listCategory, categorizedArticles, articles]);

  // Clearing the search is what makes `articles` the full set again rather than
  // the last search's hits, which is what this view lists.
  const openList = (category) => {
    setListCategory(category);
    setViewMode('list');
    setSearchQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    setViewMode('article');
    setFeedbackGiven(false);

    // Opens were not tracked at all before this — the only Ahoy call in the
    // Help Center was 'Help Article Feedback', which fires on thumbs up/down and
    // so only ever measures the small minority who respond. `view_count` exists
    // on educational_contents and is exposed on HelpCenterArticleType, but
    // nothing increments it. Without an open event there is no denominator: no
    // way to tell an article nobody reads from one everybody reads and nobody
    // rates. Mirrors the feedback payload so the two join on article_id.
    const { ahoy } = window;
    if (ahoy && ahoy.track) {
      ahoy.track('Help Article Opened', {
        article_id: article?.id,
        article_title: article?.title,
        article_category: article?.category,
      });
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToHome = () => {
    setViewMode('home');
    setSelectedArticle(null);
    setListCategory(null);
    setSearchQuery('');
  };

  // The Help Center is its own React root, mounted by succession/help.html.erb;
  // the support drawer is a different root mounted by the layout. They cannot
  // share state, so this crosses the gap the same way the tour replay already
  // does (SupportHelpDrawer dispatches 'replay-tour').
  const openSupportDrawer = () => {
    window.dispatchEvent(new CustomEvent('open-support-drawer'));
  };

  const handleFeedback = (helpful) => {
    setFeedbackGiven(true);
    // Track feedback via Ahoy analytics
    const ahoy = window.ahoy;
    if (ahoy && ahoy.track) {
      ahoy.track('Help Article Feedback', {
        article_id: selectedArticle?.id,
        article_title: selectedArticle?.title,
        article_category: selectedArticle?.category,
        helpful: helpful,
      });
    }
  };

  /* ─── Home View ─── */
  if (viewMode === 'home') {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-3" style={{ color: 'var(--succession-text-primary, #111827)' }}>{intl.formatMessage({ id: 'succession.help_center.title' })}</h1>
          <p className="text-lg mb-8" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
            {intl.formatMessage({ id: 'succession.help_center.subtitle' })}
          </p>

          {/* Canon SearchBar */}
          <div className="max-w-2xl mx-auto">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={intl.formatMessage({ id: 'succession.help_center.search_placeholder' })}
              variant="outline"
              size="lg"
            />
          </div>
        </div>

        {loading && (
          <div className="text-center py-12" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.help_center.loading_articles' })}</div>
        )}

        {/* Search Results or Categories */}
        {searchQuery.trim() ? (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--succession-text-primary, #111827)' }}>
              {intl.formatMessage({ id: 'succession.help_center.search_results' })} ({articles.length})
            </h2>
            {articles.length > 0 ? (
              <div className="space-y-4">
                {articles.map((article) => (
                  <ArticleRow key={article.id} article={article} onClick={handleArticleClick} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg p-12 text-center" style={cardStyle}>
                <p className="mb-4" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.help_center.no_articles_found' }, { query: searchQuery })}</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-4 py-2 rounded-md text-sm transition-colors duration-300"
                  style={{ border: '1px solid var(--succession-border, #e5e7eb)', color: 'var(--succession-text-secondary, #6b7280)' }}
                >
                  {intl.formatMessage({ id: 'succession.help_center.clear_search' })}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Category Grid */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--succession-text-primary, #111827)' }}>{intl.formatMessage({ id: 'succession.help_center.browse_by_category' })}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((cat) => {
                  const catArticles = categorizedArticles[cat] || [];
                  return (
                    <div
                      key={cat}
                      className="rounded-lg p-6 cursor-pointer group"
                      style={cardStyle}
                      onClick={() => openList(cat)}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--succession-shadow-lg, 0 10px 15px -3px rgba(0,0,0,0.1))'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300" style={{ backgroundColor: iconBg }}>
                        <CategoryIcon category={cat} className="w-7 h-7" style={{ color: iconColor }} />
                      </div>
                      <h3 className="text-xl font-semibold mb-1" style={{ color: 'var(--succession-text-primary, #111827)' }}>{formatCategoryName(cat)}</h3>
                      <p className="text-sm mb-2" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.help_center.article_count' }, { count: catArticles.length })}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Popular Articles */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <svg className="w-6 h-6" style={{ color: iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                <h2 className="text-2xl font-bold" style={{ color: 'var(--succession-text-primary, #111827)' }}>{intl.formatMessage({ id: 'succession.help_center.popular_articles' })}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {popularArticles.map((article) => (
                  <div
                    key={article.id}
                    className="rounded-lg p-6 cursor-pointer"
                    style={cardStyle}
                    onClick={() => handleArticleClick(article)}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--succession-shadow, 0 4px 6px -1px rgba(0,0,0,0.1))'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: iconBg }}>
                        <CategoryIcon category={article.category} className="w-6 h-6" style={{ color: iconColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <h3 className="font-semibold" style={{ color: 'var(--succession-text-primary, #111827)' }}>{article.title}</h3>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium flex-shrink-0" style={badgeStyle}>{formatCategoryName(article.category)}</span>
                        </div>
                        <p className="text-sm line-clamp-2" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{article.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ─── Category / All Articles List View ─── */
  if (viewMode === 'list') {
    const heading = listCategory
      ? formatCategoryName(listCategory)
      : intl.formatMessage({ id: 'succession.help_center.all_articles' });

    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-6" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>
          <button type="button" onClick={handleBackToHome} className="transition-colors duration-300" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{intl.formatMessage({ id: 'succession.help_center.help_center_breadcrumb' })}</button>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <span className="font-medium" style={{ color: 'var(--succession-text-primary, #111827)' }}>{heading}</span>
        </div>

        {/* Back Button */}
        <button
          type="button"
          onClick={handleBackToHome}
          className="flex items-center gap-2 text-sm mb-6 px-3 py-2 rounded transition-colors duration-300"
          style={{ color: 'var(--succession-text-secondary, #6b7280)' }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          {intl.formatMessage({ id: 'succession.help_center.back_to_help_center' })}
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {listCategory && (
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: iconBg }}>
              <CategoryIcon category={listCategory} className="w-6 h-6" style={{ color: iconColor }} />
            </div>
          )}
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--succession-text-primary, #111827)' }}>{heading}</h1>
            <p className="text-sm" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.help_center.article_count' }, { count: listArticles.length })}</p>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.help_center.loading_articles' })}</div>
        )}

        {!loading && (listArticles.length > 0 ? (
          <div className="space-y-4">
            {listArticles.map((article) => (
              <ArticleRow key={article.id} article={article} onClick={handleArticleClick} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg p-12 text-center" style={cardStyle}>
            <p style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.help_center.no_articles_in_category' })}</p>
          </div>
        ))}
      </div>
    );
  }

  /* ─── Article View ─── */
  if (viewMode === 'article' && selectedArticle) {
    const body = selectedArticle.mdBody || selectedArticle.md_body || selectedArticle.content || '';

    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Article Content */}
          <div className="lg:col-span-2">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-6" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>
              <button onClick={handleBackToHome} className="transition-colors duration-300" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{intl.formatMessage({ id: 'succession.help_center.help_center_breadcrumb' })}</button>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              <button type="button" onClick={() => openList(selectedArticle.category)} className="transition-colors duration-300" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{formatCategoryName(selectedArticle.category)}</button>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              <span className="font-medium truncate" style={{ color: 'var(--succession-text-primary, #111827)' }}>{selectedArticle.title}</span>
            </div>

            {/* Back Button */}
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-2 text-sm mb-6 px-3 py-2 rounded transition-colors duration-300"
              style={{ color: 'var(--succession-text-secondary, #6b7280)' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              {intl.formatMessage({ id: 'succession.help_center.back_to_help_center' })}
            </button>

            {/* Article Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: iconBg }}>
                  <CategoryIcon category={selectedArticle.category} className="w-6 h-6" style={{ color: iconColor }} />
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium" style={badgeStyle}>{formatCategoryName(selectedArticle.category)}</span>
              </div>
              <h1 className="text-4xl font-bold mb-4" style={{ color: 'var(--succession-text-primary, #111827)' }}>{selectedArticle.title}</h1>
              <p className="text-lg" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{selectedArticle.description}</p>
            </div>

            <hr className="mb-8" style={{ borderColor: 'var(--succession-border, #e5e7eb)' }} />

            {/* Article Content */}
            <div className="prose prose-slate max-w-none mb-12" dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }} />

            <hr className="mb-8" style={{ borderColor: 'var(--succession-border, #e5e7eb)' }} />

            {/* Feedback Section */}
            <div className="rounded-lg p-6" style={cardStyle}>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--succession-text-primary, #111827)' }}>{intl.formatMessage({ id: 'succession.help_center.was_helpful' })}</h3>
              {!feedbackGiven ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => handleFeedback(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors duration-300"
                    style={{ border: '1px solid var(--succession-border, #e5e7eb)', color: 'var(--succession-text-secondary, #6b7280)' }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21H7a2 2 0 01-2-2v-7a2 2 0 012-2h.5L12 3l2 7z" /></svg>
                    {intl.formatMessage({ id: 'succession.help_center.yes_helpful' })}
                  </button>
                  <button
                    onClick={() => handleFeedback(false)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors duration-300"
                    style={{ border: '1px solid var(--succession-border, #e5e7eb)', color: 'var(--succession-text-secondary, #6b7280)' }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.737 3H17a2 2 0 012 2v7a2 2 0 01-2 2h-.5L12 21l-2-7z" /></svg>
                    {intl.formatMessage({ id: 'succession.help_center.no_need_help' })}
                  </button>
                </div>
              ) : (
                <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--succession-bg-secondary, #f9fafb)' }}>
                  <p className="text-sm" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>{intl.formatMessage({ id: 'succession.help_center.feedback_thank_you' })}</p>
                </div>
              )}
            </div>

            {/* Contact Support CTA */}
            {supportEnabled && (
            <div className="rounded-lg p-6 mt-6" style={{ border: '1px solid rgba(var(--succession-primary-rgb, 84,137,182), 0.25)', backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: iconBg }}>
                  <svg className="w-6 h-6" style={{ color: iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--succession-text-primary, #111827)' }}>{intl.formatMessage({ id: 'succession.help_center.still_need_help' })}</h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{intl.formatMessage({ id: 'succession.help_center.support_cta_body' })}</p>
                  <button
                    onClick={openSupportDrawer}
                    className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-md text-sm transition-opacity duration-300"
                    style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                    {intl.formatMessage({ id: 'succession.help_center.contact_support' })}
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-lg" style={cardStyle}>
                <div className="p-4" style={{ borderBottom: '1px solid var(--succession-border, #e5e7eb)' }}>
                  <h3 className="font-semibold" style={{ color: 'var(--succession-text-primary, #111827)' }}>{intl.formatMessage({ id: 'succession.help_center.quick_links' })}</h3>
                </div>
                <div className="p-4 space-y-2">
                  <button
                    onClick={() => openList(null)}
                    className="w-full text-left px-3 py-2 text-sm rounded transition-colors duration-300"
                    style={{ color: 'var(--succession-text-secondary, #6b7280)' }}
                  >
                    {intl.formatMessage({ id: 'succession.help_center.browse_all_articles' })}
                  </button>
                  {supportEnabled && (
                    <button
                      onClick={openSupportDrawer}
                      className="w-full text-left px-3 py-2 text-sm rounded transition-colors duration-300"
                      style={{ color: 'var(--succession-text-secondary, #6b7280)' }}
                    >
                      {intl.formatMessage({ id: 'succession.help_center.contact_support' })}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

HelpCenterInner.propTypes = {
  supportEnabled: PropTypes.bool,
};

HelpCenterInner.defaultProps = {
  supportEnabled: false,
};

const HelpCenter = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  const locale = window.locale;
  return (
    <ApolloProvider client={client}>
      <IntlProvider locale={locale} messages={translations[locale] || translations.en}>
        <HelpCenterInner {...props} />
      </IntlProvider>
    </ApolloProvider>
  );
};

export default HelpCenter;
